// src/components/admin/page-management/section-forms/NewsletterForm.js
import React, { useState } from 'react';
import ColorPicker from '../../shared/ColorPicker';
import MediaUploader from '../../shared/MediaUploader';
import { ChevronDown, ChevronUp } from 'lucide-react';

const NewsletterForm = ({ content, updateContent }) => {
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Newsletter Section</h3>
      
      {/* Content Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Content</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Title</label>
          <input
            type="text"
            value={content?.title || 'Join Our Newsletter'}
            onChange={(e) => updateContent('title', e.target.value)}
            className="w-full p-2 border rounded"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Subtitle</label>
          <textarea
            value={content?.subtitle || 'Stay updated with our latest products, promotions, and packaging tips.'}
            onChange={(e) => updateContent('subtitle', e.target.value)}
            className="w-full p-2 border rounded"
            rows="2"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Button Text</label>
            <input
              type="text"
              value={content?.buttonText || 'Subscribe'}
              onChange={(e) => updateContent('buttonText', e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Input Placeholder</label>
            <input
              type="text"
              value={content?.placeholderText || 'Enter your email'}
              onChange={(e) => updateContent('placeholderText', e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Success Message</label>
          <input
            type="text"
            value={content?.successMessage || 'Thank you for subscribing!'}
            onChange={(e) => updateContent('successMessage', e.target.value)}
            className="w-full p-2 border rounded"
          />
        </div>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showPrivacyText !== false}
              onChange={(e) => updateContent('showPrivacyText', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Privacy Text</span>
          </label>
        </div>
        
        {content?.showPrivacyText !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Privacy Text</label>
            <textarea
              value={content?.privacyText || 'We respect your privacy and will never share your information.'}
              onChange={(e) => updateContent('privacyText', e.target.value)}
              className="w-full p-2 border rounded"
              rows="2"
            />
          </div>
        )}
      </div>
      
      {/* Styling Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Styling</h4>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.hasBackgroundImage || false}
              onChange={(e) => updateContent('hasBackgroundImage', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Use Background Image</span>
          </label>
        </div>
        
        {content?.hasBackgroundImage && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Background Image</label>
            <MediaUploader
              type="image"
              value={content?.backgroundImageUrl || ''}
              onUploadComplete={(url) => updateContent('backgroundImageUrl', url)}
              onRemove={() => updateContent('backgroundImageUrl', '')}
              className="mb-2"
            />
            
            <div className="mt-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.overlayEnabled || false}
                  onChange={(e) => updateContent('overlayEnabled', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Add Color Overlay</span>
              </label>
            </div>
            
            {content?.overlayEnabled && (
              <div className="mt-2">
                <label className="block text-sm font-medium mb-1">Overlay Color</label>
                <ColorPicker
                  color={content?.overlayColor || 'rgba(0,0,0,0.5)'}
                  onChange={(color) => updateContent('overlayColor', color)}
                />
              </div>
            )}
          </div>
        )}
        
        {!content?.hasBackgroundImage && (
          <>
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.backgroundGradient || false}
                  onChange={(e) => updateContent('backgroundGradient', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Use Gradient Background</span>
              </label>
            </div>
            
            {content?.backgroundGradient ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Gradient Start Color</label>
                  <ColorPicker
                    color={content?.gradientFrom || '#f9fafb'}
                    onChange={(color) => updateContent('gradientFrom', color)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Gradient End Color</label>
                  <ColorPicker
                    color={content?.gradientTo || '#f3f4f6'}
                    onChange={(color) => updateContent('gradientTo', color)}
                  />
                </div>
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Background Color</label>
                <ColorPicker
                  color={content?.backgroundColor || '#f3f4f6'}
                  onChange={(color) => updateContent('backgroundColor', color)}
                />
              </div>
            )}
          </>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Text Color</label>
            <ColorPicker
              color={content?.textColor || '#111827'}
              onChange={(color) => updateContent('textColor', color)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subtitle Color</label>
            <ColorPicker
              color={content?.subtitleColor || '#4b5563'}
              onChange={(color) => updateContent('subtitleColor', color)}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Button Color</label>
            <ColorPicker
              color={content?.buttonColor || '#013263'}
              onChange={(color) => updateContent('buttonColor', color)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Button Text Color</label>
            <ColorPicker
              color={content?.buttonTextColor || '#ffffff'}
              onChange={(color) => updateContent('buttonTextColor', color)}
            />
          </div>
        </div>
        
        <button
          type="button"
          className="mb-4 text-blue-600 hover:text-blue-800 font-medium flex items-center"
          onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
        >
          {showAdvancedSettings ? (
            <>
              <ChevronUp className="h-5 w-5 mr-1" />
              Hide Advanced Settings
            </>
          ) : (
            <>
              <ChevronDown className="h-5 w-5 mr-1" />
              Show Advanced Settings
            </>
          )}
        </button>
        
        {showAdvancedSettings && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Input Background</label>
                <ColorPicker
                  color={content?.inputBackgroundColor || '#ffffff'}
                  onChange={(color) => updateContent('inputBackgroundColor', color)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Input Text</label>
                <ColorPicker
                  color={content?.inputTextColor || '#1f2937'}
                  onChange={(color) => updateContent('inputTextColor', color)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Input Border</label>
                <ColorPicker
                  color={content?.inputBorderColor || '#d1d5db'}
                  onChange={(color) => updateContent('inputBorderColor', color)}
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Border Radius</label>
              <input
                type="text"
                value={content?.borderRadius || '0.375rem'}
                onChange={(e) => updateContent('borderRadius', e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="e.g., 0.375rem, 6px"
              />
            </div>
            
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="rounded-input"
                checked={content?.roundedInput !== false}
                onChange={(e) => updateContent('roundedInput', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="rounded-input" className="text-sm">Rounded Input Field</label>
            </div>
            
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="show-icon"
                checked={content?.showIcon !== false}
                onChange={(e) => updateContent('showIcon', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="show-icon" className="text-sm">Show Email Icon</label>
            </div>
            
            {content?.showIcon !== false && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Icon Color</label>
                <ColorPicker
                  color={content?.iconColor || '#374151'}
                  onChange={(color) => updateContent('iconColor', color)}
                />
              </div>
            )}
            
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="button-hover"
                checked={content?.buttonHoverEffect !== false}
                onChange={(e) => updateContent('buttonHoverEffect', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="button-hover" className="text-sm">Button Hover Effect</label>
            </div>
            
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="box-shadow"
                checked={content?.boxShadow || false}
                onChange={(e) => updateContent('boxShadow', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="box-shadow" className="text-sm">Add Box Shadow</label>
            </div>
            
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="show-divider"
                checked={content?.showDivider !== false}
                onChange={(e) => updateContent('showDivider', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="show-divider" className="text-sm">Show Divider Below Title</label>
            </div>
            
            {content?.showDivider !== false && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Divider Color</label>
                <ColorPicker
                  color={content?.dividerColor || '#e5e7eb'}
                  onChange={(color) => updateContent('dividerColor', color)}
                />
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Maximum Width</label>
              <input
                type="text"
                value={content?.maxWidth || '36rem'}
                onChange={(e) => updateContent('maxWidth', e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="e.g., 36rem, 576px"
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum width of the subscription form
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Spacing & Animation */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Spacing & Animation</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Top Padding</label>
            <input
              type="text"
              value={content?.paddingTop || '4rem'}
              onChange={(e) => updateContent('paddingTop', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 4rem, 64px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Bottom Padding</label>
            <input
              type="text"
              value={content?.paddingBottom || '4rem'}
              onChange={(e) => updateContent('paddingBottom', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 4rem, 64px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Bottom Margin</label>
            <input
              type="text"
              value={content?.marginBottom || '3rem'}
              onChange={(e) => updateContent('marginBottom', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 3rem, 48px"
            />
          </div>
        </div>
        
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            id="animation-enabled"
            checked={content?.animationEnabled !== false}
            onChange={(e) => updateContent('animationEnabled', e.target.checked)}
            className="mr-2"
          />
          <label htmlFor="animation-enabled" className="text-sm">Enable Animation</label>
        </div>
      </div>
    </div>
  );
};

export default NewsletterForm;